/**
 * Stable feature keys for plan entitlements.
 * Theme settings reuse theme.settings key names; app features use `app.*`.
 *
 * Keep in sync with backend `PlanEntitlements` (Ruby) for enforceOnSave keys.
 */
export const PlanFeatureKeys = {
  // —— Theme settings: Starter+ ——
  IS_ITEM_COMPARE_AT_PRICE: 'is_item_compare_at_price',
  IS_SUBTOTAL_EX_TAX: 'is_subtotal_ex_tax',
  IS_SUBTOTAL_INC_DISCOUNT: 'is_subtotal_inc_discount',
  IS_ITEM_SUBTOTAL_INC_DISCOUNT: 'is_item_subtotal_inc_discount',
  SHOW_FOOTER_TERM: 'show_footer_term',
  SHOW_INVOICE_EXTRA_OPTIONS: 'show_invoice_extra_options',
  SHOW_TRACKING_INFO: 'show_tracking_info',
  SHOW_FULFILL_LOCATION: 'show_fulfill_location',
  IS_ITEM_WEIGHT_UNIT: 'is_item_weight_unit',
  IS_TOTAL_SHIPPING: 'is_total_shipping',
  IS_TOTAL_SHIPPING_TAX: 'is_total_shipping_tax',
  LINE_ITEM_PADDING: 'line_item_padding',
  LINE_ITEM_TRANSLATE: 'line_item_translate',
  SUPPLIER_EXTRA_FIELDS: 'supplier_extra_fields',
  TEMPLATE_CURRENCY_BILLING: 'template_currency_billing',
  SAVE_AS_TEMPLATE: 'save_as_template',
  PACKING_LOCATION_NAV: 'packing_location_nav',
  PACKING_TRACKING_NAV: 'packing_tracking_nav',
  FOOTER_NOTE: 'footer_note',

  // —— Theme settings: Professional+ ——
  THANK_YOU_NOTE: 'thank_you_note',
  SHOW_TIMELINE: 'show_timeline',
  TIMELINE_SEPARATE_PAGE: 'timeline_separate_page',
  SHOW_ORDER_STATUS_URL: 'show_order_status_url',
  SHOW_HS_CODE: 'show_hs_code',
  SHOW_ITEM_METAFIELDS: 'show_item_metafields',
  LINE_ITEM_METAFIELD_TRANSLATE: 'line_item_metafield_translate',
  IS_ITEM_PROPERTIES: 'is_item_properties',
  IS_ITEM_PROPERTIES_ORDER: 'is_item_properties_order',
  IS_PRICE_AFTER_DISCOUNT: 'is_price_after_discount',
  SHOW_VARIANT_METAFIELDS: 'show_variant_metafields',
  SHOW_USED_GIFT_CARDS: 'show_used_gift_cards',
  SHOW_GIFT_CARD_CODE: 'show_gift_card_code',
  IS_TOTAL_REFUND: 'is_total_refund',
  SHOW_TAX_LINE: 'show_tax_line',
  SHOW_PAYMENT_TERMS: 'show_payment_terms',
  SHOW_PO_NUMBER: 'show_po_number',
  SHOW_ORDER_METAFIELDS: 'show_order_metafields',
  SHOW_CUSTOMER_METAFIELDS: 'show_customer_metafields',
  SHOW_SUPPLIER_EXTRA: 'show_supplier_extra',
  IS_TAX_NUMBER: 'is_tax_number',
  IS_ITEM_SUBTOTAL_EX_TAX: 'is_item_subtotal_ex_tax',
  IS_NET_SHIPPING: 'is_net_shipping',
  INVOICE_EXTRA_OPTIONS_FIELDS: 'invoice_extra_options_fields',
  CUSTOM_FIELDS_SECTION: 'custom_fields_section',
  PAGE_ATTACHMENTS: 'page_attachments',
  PACKING_TIMELINE_NAV: 'packing_timeline_nav',
  CUSTOM_CSS: 'custom_css',

  // —— Theme settings: Premium ——
  SHOW_IN_STOCK: 'show_in_stock',
  IS_REFUND_NO: 'is_refund_no',
  IS_BILLING_COMPANY_ID: 'is_billing_company_id',
  IS_SHIPPING_COMPANY_ID: 'is_shipping_company_id',
  IS_BILLING_LOCATION_TAX_ID: 'is_billing_location_tax_id',
  IS_SHIPPING_LOCATION_TAX_ID: 'is_shipping_location_tax_id',
  TEMPLATE_CURRENCY_ALL: 'template_currency_all',

  // —— App features ——
  APP_AUTOMATION_EMAILS: 'app.automation_emails',
  APP_CUSTOM_SENDER_EMAIL: 'app.custom_sender_email',
  APP_SHOPIFY_FLOW: 'app.shopify_flow',
  APP_EMAIL_BCC_CC: 'app.email_bcc_cc',
  APP_CUSTOM_INVOICE_FLAGS: 'app.custom_invoice_flags',
  APP_EDIT_THEME_CODE: 'app.edit_theme_code',
  APP_BULK_PDF_DOWNLOAD: 'app.bulk_pdf_download',
  APP_DUAL_CURRENCY_LIQUID: 'app.dual_currency_liquid',
  APP_VAT_AUTO: 'app.vat_auto',
} as const

export type PlanFeatureKey =
  (typeof PlanFeatureKeys)[keyof typeof PlanFeatureKeys]
